/**
 * OptiPlace — what an operator may be told when smart-assign refuses.
 *
 * supabase-js turns every non-2xx Edge response into a FunctionsHttpError
 * whose message is the useless "Edge Function returned a non-2xx status
 * code"; the Response itself, unread, is on `error.context`. smart-assign's
 * refusals use the canonical errResponse body ({ success:false, ok:false,
 * error, ...details }), and some of them are exactly what the operator needs —
 * e.g. "All suggested start times are in the past. Please select a future
 * appointment time or refresh the page." (incident 2026-10-05, Floyd's).
 *
 * This is deliberately NOT "show whatever the server said". The text is shown
 * only when ALL hold:
 *   • the error is a FunctionsHttpError carrying a Response;
 *   • the status is a 4xx refusal other than 401 / 403 (5xx bodies carry raw
 *     exception text; auth failures say nothing useful to an operator);
 *   • the body is JSON (≤ 8 KB) in the canonical refusal shape (ok / success
 *     === false) with a string `error`;
 *   • `error` (and an optional string `details`) is one short line (≤ 300
 *     chars) with nothing that looks like a stack, SQL / database text, a
 *     token, a URL or structured data.
 * Otherwise — network failure, relay error, 5xx, auth, non-JSON, odd shape,
 * anything thrown locally — the fixed fallback is shown.
 *
 * The Response is read through `clone()`, so the original stays unconsumed
 * for anything else that may want it. Never throws.
 */
export const SMART_ASSIGN_FALLBACK_MESSAGE = 'Failed to analyze technician assignments.';

const MAX_TEXT = 300;
const MAX_BODY = 8192;
const UNSAFE = /(\bat\s+\S+:\d+|:\d+:\d+|\b[A-Za-z]*Error:|\bstack\b|\btrace\b|exception|\bsql\b|postgres|pgrst|\brelation\b|\bcolumn\b|syntax error|violates|constraint|bearer|eyJ[A-Za-z0-9_-]{8,}|service_role|apikey|password|secret|token|https?:\/\/|<\/?[a-z]|[{}[\]`])/i;

function safeLine(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.replace(/\s+/g, ' ').trim();
  if (!text || text.length > MAX_TEXT || UNSAFE.test(text)) return null;
  return text;
}

export async function smartAssignRefusalMessage(error: unknown): Promise<string> {
  return edgeRefusalMessage(error, SMART_ASSIGN_FALLBACK_MESSAGE);
}

/**
 * The same safe reader for any Edge function using the canonical refusal shape, with the caller's
 * own fallback. (Core-100: the split surfaces showed only "Failed to split ticket" for every
 * refusal, including Due Out and shift-end refusals the operator can act on.)
 */
export async function edgeRefusalMessage(error: unknown, fallback: string): Promise<string> {
  try {
    const e = error as { name?: unknown; context?: unknown } | null;
    if (!e || e.name !== 'FunctionsHttpError') return fallback;
    const res = e.context as Response | undefined;
    if (!res || typeof res.status !== 'number' || typeof res.clone !== 'function') return fallback;
    if (res.status < 400 || res.status >= 500 || res.status === 401 || res.status === 403) return fallback;
    if (!/application\/json/i.test(res.headers?.get?.('content-type') ?? '')) return fallback;

    const raw = await res.clone().text();
    if (raw.length > MAX_BODY) return fallback;
    const body: unknown = JSON.parse(raw);
    if (!body || typeof body !== 'object' || Array.isArray(body)) return fallback;
    const b = body as Record<string, unknown>;
    if (b.ok !== false && b.success !== false) return fallback;

    const message = safeLine(b.error);
    if (!message) return fallback;
    const details = safeLine(b.details);
    return details && details !== message ? `${message} ${details}` : message;
  } catch {
    return fallback;
  }
}
